// AI 로켓등록 문서 만들기 — 등록번호·옵션·가격·쿠팡 문서(coupangDoc) 틀.
// AIRocketReg.vue 에서 그대로 옮겼다. 소싱관리 «상세 보기» 창의 AI 등록 초안도 같은 메서드로 만든다(값이 갈라지지 않게).
// 쓰는 쪽 this 에 defaultSettings 가 있어야 한다.
import axios from 'axios';
import pricingPolicy from '../services/works/pricingPolicy';
import { pickKeptSkus, KEEP_NONE_MESSAGE, dropExcludedSkuImages } from '../utils/keepSkus';

export default {
    methods: {
        // coupangDoc 초기화 함수 개선 - 옵션 정보를 통합하여 하나의 문서로 생성
        initializeCoupangDoc(createResult, defaultSettings, rocketRegInfo = null, options = null) {
            // createResult 확인
            if (!createResult) {
                console.error('initializeCoupangDoc: createResult가 없습니다.');
                return {};
            }

            // rocketRegInfo 정보 확인 (직접 전달되지 않았다면 createResult에서 찾기)
            const regInfo = rocketRegInfo || createResult.rocketRegInfo || this.extensionResponseData?.rocketRegInfo;

            // 최종 coupangDoc 구조의 기본 틀 생성
            let coupangDoc = {
                docId: createResult.docId,
                productName: createResult.jsonDocument?.[0]?.startPage?.productName || "",
                categoryPath: createResult.categoryPath || createResult.jsonDocument?.[0]?.startPage?.categoryPath || "",
                categoryId: createResult.categoryId,
                displayCategoryCode: createResult.displayCategoryCode,
                jsonDocument: [], // 빈 배열로 초기화 (옵션별 문서를 추가할 예정)
                scope: createResult.scope || "Retail_Categorized_Single",
                productNoticeNumber: createResult.productNoticeNumber,
                version: createResult.version,
                remark: createResult.remark
            };

            try {
                // 스키마 정보가 있는지 확인
                let schema = null;
                let schemaString = null;

                // 다양한 소스에서 스키마 정보 추출 시도 (우선순위 적용)
                if (createResult.schemaString) {
                    schemaString = createResult.schemaString;
                } else if (regInfo?.schemaString) {
                    schemaString = regInfo.schemaString;
                } else if (this.extensionResponseData?.rocketRegInfo?.schemaString) {
                    schemaString = this.extensionResponseData.rocketRegInfo.schemaString;
                }

                // 스키마 문자열이 있으면 파싱
                if (schemaString) {
                    try {
                        schema = JSON.parse(schemaString);
                        console.log('스키마 정보 파싱 성공:', schema.required?.length || 0, '개 필수 필드');

                        // 🆕 스키마 정보 추출 및 저장 (rocketRegInfo에 저장)
                        const extractedSchemaInfo = this.extractSchemaInfoForQuotation(schema);

                        // rocketRegInfo에 schemaInfo 추가 (coupangDoc이 아닌 rocketRegInfo에 저장)
                        if (!regInfo.schemaInfo) {
                            regInfo.schemaInfo = extractedSchemaInfo;
                        }

                        console.log('✅ 스키마 정보 추출 완료 (rocketRegInfo에 저장):', {
                            exposedCount: extractedSchemaInfo.exposedAttributes.length,
                            unexposedCount: extractedSchemaInfo.unexposedAttributes.length,
                            logisticsCount: extractedSchemaInfo.logisticsInfo.length,
                            legalCount: extractedSchemaInfo.legalInfo.length
                        });
                    } catch (parseError) {
                        console.error('스키마 문자열 파싱 실패:', parseError);
                    }
                } else {
                    console.warn('스키마 정보를 찾을 수 없습니다.');
                }

                // 옵션이 있는 경우 각 옵션별 jsonDocument 항목 생성 (개선된 방식)
                if (options && options.length > 0) {
                    console.log(`${options.length}개 옵션에 대한 통합 coupangDoc 생성 시작 (스키마 기반 동일 구조)`);

                    // 🆕 1단계: 모든 옵션에 공통으로 적용할 기본 구조 생성
                    const baseDocStructure = {};

                    // createResult의 jsonDocument[0].startPage가 있으면 복사하여 기본 구조 생성
                    if (createResult.jsonDocument?.[0]?.startPage) {
                        baseDocStructure.startPage = {
                            ...createResult.jsonDocument[0].startPage
                        };
                        // 기본 상품명 설정 (옵션명 제외)
                        baseDocStructure.startPage.productName = coupangDoc.productName;
                    } else {
                        // 기본 startPage 생성
                        baseDocStructure.startPage = {
                            productName: coupangDoc.productName,
                            categoryPath: coupangDoc.categoryPath
                        };
                    }

                    // 🆕 2단계: 스키마 기반으로 완전한 구조 생성 (모든 옵션에 동일하게 적용)
                    if (schema) {
                        console.log('📋 스키마 기반 기본 구조 생성 중...');
                        this.extractAndApplySchema(baseDocStructure, schema);
                        console.log('✅ 스키마 기반 기본 구조 생성 완료');
                    }

                    // 🆕 3단계: imagePage 기본 구조 설정
                    if (!baseDocStructure.imagePage) {
                        baseDocStructure.imagePage = {
                            images: { mainImage: "", additionalImage: "", labelImages: [] },
                            details: { detailedImage: "", htmlProductDetailContent: "", altText: "" },
                            msrpAgree: true,
                            // 쿠팡이 나중에 추가한 필수 동의. 없으면 등록 전송이
                            // 400 "Label contact disclosure agreement is required." 로 막힌다.
                            // ⚠ validate-step 2~5 는 이 필드를 보지 않는다 —
                            //    4단계를 다 통과해도 최종 전송에서 막힌다(2026-09-23 실측).
                            labelContactAgreed: true
                        };
                    } else if (baseDocStructure.imagePage && !baseDocStructure.imagePage.msrpAgree) {
                        baseDocStructure.imagePage.msrpAgree = true;
                    }
                    // msrpAgree 와 따로 본다. 기존 문서는 msrpAgree 만 들어있어
                    // 위 else if 로는 labelContactAgreed 가 채워지지 않는다(실측 0/96).
                    if (baseDocStructure.imagePage && !baseDocStructure.imagePage.labelContactAgreed) {
                        baseDocStructure.imagePage.labelContactAgreed = true;
                    }

                    // 🆕 4단계: 상단/하단 이미지 설정 적용 (defaultSettings에서 가져옴)
                    if (defaultSettings && defaultSettings.imageSettings) {
                        this.applyImageSettingsToDocStructure(baseDocStructure, defaultSettings);
                    }

                    console.log('📋 기본 구조 생성 완료, 옵션별 적용 시작...');

                    // 🆕 5단계: 모든 옵션에 동일한 구조 적용 (최적화)
                    console.log(`📦 ${options.length}개 옵션에 동일한 스키마 구조 적용 시작`);

                    for (let i = 0; i < options.length; i++) {
                        const option = options[i];
                        console.log(`📝 옵션 ${i+1}/${options.length}: ${option.korName || option.optionNumber} 구조 생성`);

                        // 기본 구조를 깊은 복사 (모든 옵션에 동일한 구조)
                        const docItem = JSON.parse(JSON.stringify(baseDocStructure));

                        // 옵션별 기존 coupangDoc이 있는 경우 해당 정보 병합 (기존 데이터 보존)
                        if (option.coupangDoc && option.coupangDoc.jsonDocument && option.coupangDoc.jsonDocument.length > 0) {
                            const optionDoc = option.coupangDoc.jsonDocument[0];
                            console.log(`🔄 옵션 ${i+1} 기존 데이터 병합 중...`);

                            // 각 페이지 정보 병합 (startPage는 이미 처리했으므로 제외)
                            const pages = ['productPage', 'imagePage', 'legalPage', 'logisticsPage', 'sourcingPage'];

                            pages.forEach(page => {
                                if (optionDoc[page]) {
                                    // 기존 페이지 정보가 있으면 병합, 없으면 새로 생성
                                    docItem[page] = {
                                        ...(docItem[page] || {}),
                                        ...optionDoc[page]
                                    };
                                }
                            });
                        }

                        // jsonDocument 배열에 추가
                        coupangDoc.jsonDocument.push(docItem);
                    }

                    console.log(`✅ ${options.length}개 옵션에 대한 통합 coupangDoc 생성 완료 (스키마 기반 동일 구조)`);
                } else {
                    // 옵션이 없는 경우 기본 문서 하나만 생성 (개선된 방식)
                    console.log('단일 상품 coupangDoc 생성 시작 (스키마 기반)');

                    const docItem = {};

                    // createResult의 jsonDocument[0].startPage가 있으면 복사
                    if (createResult.jsonDocument?.[0]?.startPage) {
                        docItem.startPage = {
                            ...createResult.jsonDocument[0].startPage
                        };
                    } else {
                        // 기본 startPage 생성
                        docItem.startPage = {
                            productName: coupangDoc.productName,
                            categoryPath: coupangDoc.categoryPath
                        };
                    }

                    // 스키마가 있으면 필요한 구조 추출하여 적용
                    if (schema) {
                        console.log('📋 스키마 기반 단일 상품 구조 생성 중...');
                        this.extractAndApplySchema(docItem, schema);
                        console.log('✅ 스키마 기반 단일 상품 구조 생성 완료');
                    }

                    // imagePage 확인 및 기본값 설정
                    if (!docItem.imagePage) {
                        docItem.imagePage = {
                            images: { mainImage: "", additionalImage: "", labelImages: [] },
                            details: { detailedImage: "", htmlProductDetailContent: "", altText: "" },
                            msrpAgree: true,
                            // 쿠팡이 나중에 추가한 필수 동의. 없으면 등록 전송이
                            // 400 "Label contact disclosure agreement is required." 로 막힌다.
                            // ⚠ validate-step 2~5 는 이 필드를 보지 않는다 —
                            //    4단계를 다 통과해도 최종 전송에서 막힌다(2026-09-23 실측).
                            labelContactAgreed: true
                        };
                    } else if (docItem.imagePage && !docItem.imagePage.msrpAgree) {
                        docItem.imagePage.msrpAgree = true;
                    }
                    // msrpAgree 와 따로 본다. 기존 문서는 msrpAgree 만 들어있어
                    // 위 else if 로는 labelContactAgreed 가 채워지지 않는다(실측 0/96).
                    if (docItem.imagePage && !docItem.imagePage.labelContactAgreed) {
                        docItem.imagePage.labelContactAgreed = true;
                    }

                    // 상단/하단 이미지 설정 적용 (defaultSettings에서 가져옴)
                    if (defaultSettings && defaultSettings.imageSettings) {
                        this.applyImageSettingsToDocStructure(docItem, defaultSettings);
                    }

                    // jsonDocument 배열에 추가
                    coupangDoc.jsonDocument.push(docItem);
                    console.log('✅ 단일 상품 coupangDoc 생성 완료');
                }

                // defaultSettings 적용
                this.applyDefaultSettings(coupangDoc, defaultSettings);

                console.log('🎉 coupangDoc 초기화 완료 (스키마 기반 동일 구조):', coupangDoc.docId, '문서 항목 수:', coupangDoc.jsonDocument.length);
                return coupangDoc;
            } catch (error) {
                console.error('initializeCoupangDoc 오류:', error);
                return coupangDoc; // 오류 발생 시 기본 문서 반환
            }
        },

        // 🆕 quotationService에서 사용할 스키마 정보 추출
        extractSchemaInfoForQuotation(schema) {
            const schemaInfo = {
                exposedAttributes: [],
                unexposedAttributes: [],
                logisticsInfo: [],
                legalInfo: []
            };

            try {
                console.log('🔍 스키마 구조 확인:', {
                    hasProperties: !!schema.properties,
                    hasProductPage: !!schema.properties?.productPage,
                    hasCommonAttributes: !!schema.properties?.productPage?.properties?.commonAttributes,
                    hasExposedAttributes: !!schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes,
                    hasUnexposedAttributes: !!schema.properties?.productPage?.properties?.unexposedAttributes
                });

                // 노출 속성 추출 (올바른 경로 사용)
                if (schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes?.allOf) {
                    console.log('📋 노출 속성 추출 시작');
                    schema.properties.productPage.properties.commonAttributes.properties.exposedAttributes.allOf.forEach((config, index) => {
                        console.log(`🔍 노출속성 [${index}] 처리 중:`, config.contains?.properties?.attributeName?.enum?.[0]);

                        if (config.contains?.properties?.attributeName) {
                            const attributeName = config.contains.properties.attributeName.enum[0];

                            // dropdown 속성이 있을 때만 enum 값을 dropdown 필드에 저장
                            const hasDropdown = config.contains.properties.attributeValue.dropdown &&
                                config.contains.properties.attributeValue.dropdown.length > 0;
                            const hasEnum = config.contains.properties.attributeValue.enum &&
                                config.contains.properties.attributeValue.enum.length > 0;

                            const attributeInfo = {
                                name: attributeName,
                                dropdown: hasDropdown ? (hasEnum ? config.contains.properties.attributeValue.enum : config.contains.properties.attributeValue.dropdown) : null,
                                example: config.contains.properties.attributeValue.examples ?
                                    config.contains.properties.attributeValue.examples[0] : ""
                            };

                            schemaInfo.exposedAttributes.push(attributeInfo);
                            console.log(`✅ 노출속성 추가: ${attributeName}`, {
                                ...attributeInfo,
                                sourceType: hasDropdown ? (hasEnum ? 'enum→dropdown' : 'dropdown') : 'example'
                            });
                        }
                    });
                } else {
                    console.log('⚠️ 노출 속성 경로를 찾을 수 없음');
                }

                // 비노출 속성 추출 (올바른 경로 사용)
                if (schema.properties?.productPage?.properties?.unexposedAttributes?.allOf) {
                    console.log('📋 비노출 속성 추출 시작');
                    schema.properties.productPage.properties.unexposedAttributes.allOf.forEach((config, index) => {
                        console.log(`🔍 비노출속성 [${index}] 처리 중:`, config.contains?.properties?.attributeName?.enum?.[0]);

                        if (config.contains?.properties?.attributeName) {
                            const attributeName = config.contains.properties.attributeName.enum[0];

                            // dropdown 속성이 있을 때만 enum 값을 dropdown 필드에 저장
                            const hasDropdown = config.contains.properties.attributeValue.dropdown &&
                                config.contains.properties.attributeValue.dropdown.length > 0;
                            const hasEnum = config.contains.properties.attributeValue.enum &&
                                config.contains.properties.attributeValue.enum.length > 0;

                            const attributeInfo = {
                                name: attributeName,
                                dropdown: hasDropdown ? (hasEnum ? config.contains.properties.attributeValue.enum : config.contains.properties.attributeValue.dropdown) : null,
                                example: config.contains.properties.attributeValue.examples ?
                                    config.contains.properties.attributeValue.examples[0] : ""
                            };

                            schemaInfo.unexposedAttributes.push(attributeInfo);
                            console.log(`✅ 비노출속성 추가: ${attributeName}`, {
                                ...attributeInfo,
                                sourceType: hasDropdown ? (hasEnum ? 'enum→dropdown' : 'dropdown') : 'example'
                            });
                        }
                    });
                } else {
                    console.log('⚠️ 비노출 속성 경로를 찾을 수 없음');
                }

                // 물류 정보 추출 (올바른 경로 사용)
                if (schema.properties?.logisticsPage?.properties) {
                    console.log('📋 물류 정보 추출 시작');
                    if (schema.properties.logisticsPage.properties.skuUnitBoxDimension) {
                        const logisticsInfo = {
                            name: 'skuUnitBoxDimension',
                            title: '박스 크기',
                            guide: schema.properties.logisticsPage.properties.skuUnitBoxDimension.description || '',
                            example: '300*200*100'
                        };
                        schemaInfo.logisticsInfo.push(logisticsInfo);
                        console.log(`✅ 물류정보 추가: skuUnitBoxDimension`, logisticsInfo);
                    }
                    if (schema.properties.logisticsPage.properties.skuUnitBoxWeight) {
                        const logisticsInfo = {
                            name: 'skuUnitBoxWeight',
                            title: '박스 무게',
                            guide: schema.properties.logisticsPage.properties.skuUnitBoxWeight.description || '',
                            example: '1000'
                        };
                        schemaInfo.logisticsInfo.push(logisticsInfo);
                        console.log(`✅ 물류정보 추가: skuUnitBoxWeight`, logisticsInfo);
                    }
                } else {
                    console.log('⚠️ 물류 정보 경로를 찾을 수 없음');
                }

                // 법적 정보 추출 (올바른 경로 사용)
                if (schema.properties?.legalPage?.properties?.notices?.allOf) {
                    console.log('📋 법적 정보 추출 시작');
                    schema.properties.legalPage.properties.notices.allOf.forEach((config, index) => {
                        console.log(`🔍 법적정보 [${index}] 처리 중:`, config.contains?.properties?.noticeItemName?.enum?.[0]);

                        if (config.contains?.properties?.noticeItemName) {
                            const noticeName = config.contains.properties.noticeItemName.enum[0];
                            const legalInfo = {
                                name: noticeName,
                                required: true,
                                guide: config.contains.properties.noticeItemName.inputGuide || "",
                                example: config.contains.properties.noticeItemValue.examples ?
                                    config.contains.properties.noticeItemValue.examples[0] : ""
                            };
                            schemaInfo.legalInfo.push(legalInfo);
                            console.log(`✅ 법적정보 추가: ${noticeName}`, legalInfo);
                        }
                    });
                } else {
                    console.log('⚠️ 법적 정보 경로를 찾을 수 없음');
                }

                console.log('📋 스키마 정보 추출 상세:', {
                    exposedAttributes: schemaInfo.exposedAttributes.map(attr => `${attr.name} (${attr.dropdown ? 'dropdown' : 'example'})`),
                    unexposedAttributes: schemaInfo.unexposedAttributes.map(attr => `${attr.name} (${attr.dropdown ? 'dropdown' : 'example'})`),
                    logisticsInfo: schemaInfo.logisticsInfo.map(info => info.name),
                    legalInfo: schemaInfo.legalInfo.map(info => info.name)
                });

            } catch (error) {
                console.error('스키마 정보 추출 중 오류:', error);
            }

            return schemaInfo;
        },

        // 스키마와 coupangDoc 비교 로깅 - 필수 필드만 로깅하도록 수정
        logSchemaAndDocComparison(schema, coupangDoc) {
            if (!schema || !coupangDoc.jsonDocument || !coupangDoc.jsonDocument[0]) {
                console.log("스키마 또는 coupangDoc이 없어 비교할 수 없습니다.");
                return;
            }

            console.group("📋 스키마와 coupangDoc 필수 필드 비교");

            // 필수 페이지 순회
            const requiredPages = schema.required || [];
            console.log("📑 필수 페이지:", requiredPages);

            requiredPages.forEach(pageName => {
                if (!schema.properties[pageName]) return;

                const pageSchema = schema.properties[pageName];
                const pageDoc = coupangDoc.jsonDocument[0][pageName] || {};
                const requiredFields = pageSchema.required || [];

                console.group(`🔹 ${pageName} 페이지 (필수 필드: ${requiredFields.join(', ')})`);

                // 페이지 description 출력 (새로 추가)
                if (pageSchema.description) {
                    console.log(`📄 페이지 설명: ${pageSchema.description}`);
                }

                // 필수 필드만 순회
                requiredFields.forEach(propName => {
                    if (!pageSchema.properties[propName]) return;

                    const propSchema = pageSchema.properties[propName];
                    const propValue = pageDoc[propName];

                    // 특별한 타입(객체, 배열) 처리
                    if (propSchema.type === 'object' && propSchema.properties) {
                        console.group(`🔸 ${propName} (객체)`);

                        // 객체 description 출력
                        if (propSchema.description) {
                            console.log(`📄 객체 설명: ${propSchema.description}`);
                        }

                        // 객체의 필수 필드만 로깅
                        const requiredSubFields = propSchema.required || [];
                        requiredSubFields.forEach(subPropName => {
                            if (!propSchema.properties[subPropName]) return;

                            const subPropSchema = propSchema.properties[subPropName];
                            const subPropValue = propValue ? propValue[subPropName] : undefined;

                            this.logPropertyComparison(subPropName, subPropSchema, subPropValue);
                        });

                        console.groupEnd();
                    } else if (propSchema.type === 'array') {
                        console.group(`🔸 ${propName} (배열)`);

                        // 배열 description 출력
                        if (propSchema.description) {
                            console.log(`📄 배열 설명: ${propSchema.description}`);
                        }

                        // 배열 스키마 정보 - allOf가 있는 경우만 처리
                        if (propSchema.allOf) {
                            console.log("📌 필수 배열 항목 스키마 정의:", propSchema.allOf.length, "개");

                            propSchema.allOf.forEach((itemSchema, index) => {
                                console.group(`📎 항목 스키마 #${index + 1}`);
                                if (itemSchema.contains && itemSchema.contains.properties) {
                                    Object.keys(itemSchema.contains.properties).forEach(itemPropName => {
                                        console.log(`   - ${itemPropName}:`, itemSchema.contains.properties[itemPropName]);
                                    });
                                }
                                console.groupEnd();
                            });
                        }

                        // 실제 배열 값
                        if (Array.isArray(propValue)) {
                            console.log("📊 실제 배열 값:", propValue.length, "개");
                            propValue.forEach((item, index) => {
                                console.log(`   #${index + 1}:`, item);
                            });
                        } else {
                            console.log("⚠️ 배열이 없거나 비어 있습니다.");
                        }

                        console.groupEnd();
                    } else {
                        // 일반 속성
                        this.logPropertyComparison(propName, propSchema, propValue);
                    }
                });

                console.groupEnd(); // 페이지 그룹 종료
            });

            console.groupEnd(); // 전체 그룹 종료
        },

        // 개별 속성 비교 로깅 (description 정보 추가)
        logPropertyComparison(propName, propSchema, propValue) {
            const schemaInfo = [];

            // 스키마 정보 수집
            if (propSchema.type) {
                schemaInfo.push(`타입: ${Array.isArray(propSchema.type) ? propSchema.type.join('/') : propSchema.type}`);
            }

            if (propSchema.dropdown && propSchema.dropdown.length) {
                schemaInfo.push(`드롭다운: [${propSchema.dropdown.join(', ')}]`);
            } else if (propSchema.enum && propSchema.enum.length) {
                schemaInfo.push(`ENUM: [${propSchema.enum.join(', ')}]`);
            }

            if (propSchema.requirement) {
                schemaInfo.push(`필수여부: ${propSchema.requirement}`);
            }

            // 값 상태 표시
            let valueStatus = '';
            if (propSchema.dropdown && propSchema.dropdown.length) {
                // 드롭다운 목록에 있는지 확인
                valueStatus = propSchema.dropdown.includes(propValue)
                    ? '✅ 유효함'
                    : '❌ 유효하지 않음 (드롭다운 목록에 없음)';
            } else if (propSchema.enum && propSchema.enum.length) {
                // enum 목록에 있는지 확인
                valueStatus = propSchema.enum.includes(propValue)
                    ? '✅ 유효함'
                    : '❌ 유효하지 않음 (ENUM 목록에 없음)';
            } else if (propValue === undefined || propValue === null) {
                valueStatus = propSchema.requirement === '필수'
                    ? '❌ 필수값 누락'
                    : '⚠️ 값 없음 (선택사항)';
            } else {
                valueStatus = '✅ 설정됨';
            }

            // 콘솔 출력
            console.group(`🔹 ${propName}`);
            console.log(`📝 스키마 정보: ${schemaInfo.join(', ')}`);

            // description 정보가 있으면 출력 (새로 추가)
            if (propSchema.description) {
                console.log(`📄 설명: ${propSchema.description}`);
            }

            // inputGuide 정보가 있으면 출력 (새로 추가)
            if (propSchema.inputGuide) {
                console.log(`📋 입력 가이드: ${propSchema.inputGuide}`);
            }

            // inputHint 정보가 있으면 출력 (새로 추가)
            if (propSchema.inputHint) {
                console.log(`💡 입력 힌트: ${propSchema.inputHint}`);
            }

            console.log(`📊 설정된 값: ${JSON.stringify(propValue)}`);
            console.log(`🔍 상태: ${valueStatus}`);
            console.groupEnd();
        },

        // 스키마에서 필요한 구조 추출 및 적용 (개선된 버전 - 완전한 구조 생성)
        extractAndApplySchema(targetDoc, schema) {
            // schema.required에서 필요한 페이지 목록 가져오기
            const pages = schema.required || [];
            console.log("📋 필수 페이지:", pages);

            pages.forEach(page => {
                if (schema.properties && schema.properties[page]) {
                    const pageSchema = schema.properties[page];

                    // 해당 페이지가 targetDoc에 없으면 생성
                    if (!targetDoc[page]) {
                        targetDoc[page] = {};
                    }

                    // 페이지 속성을 분석해서 필요한 구조 추가 (필수 필드 중심)
                    if (pageSchema.required && pageSchema.properties) {
                        console.log(`📝 ${page} 페이지 필수 필드:`, pageSchema.required);

                        // 필수 필드만 처리 (required 체크 추가)
                        pageSchema.required.forEach(fieldName => {
                            if (pageSchema.properties[fieldName]) {
                                this.createFieldStructure(targetDoc[page], fieldName, pageSchema.properties[fieldName], true);
                            }
                        });

                        console.log(`✅ ${page} 페이지: 필수 필드 ${pageSchema.required.length}개 생성 완료`);
                    }

                    // 🆕 배열 필드의 경우 스키마 기반으로 완전한 구조 생성
                    if (pageSchema.properties) {
                        Object.keys(pageSchema.properties).forEach(fieldName => {
                            const fieldSchema = pageSchema.properties[fieldName];
                            if (fieldSchema.type === 'array' && fieldSchema.allOf) {
                                this.createCompleteArrayStructure(targetDoc[page], fieldName, fieldSchema);
                            }
                        });
                    }
                }
            });
        },

        // 필드 구조 생성 (새 메서드) - required 필드만 생성하도록 수정
        createFieldStructure(targetObj, fieldName, fieldSchema, isRequired = true) {
            // 이미 필드가 존재하면 그대로 유지
            if (targetObj[fieldName] !== undefined) {
                return;
            }

            // required가 아닌 필드는 생성하지 않음 (선택적 필드 제외)
            if (!isRequired) {
                console.log(`⚠️ 선택적 필드 '${fieldName}' 생성 건너뜀 (required가 아님)`);
                return;
            }

            // 객체인 경우 재귀적으로 처리
            if (fieldSchema.type === 'object') {
                targetObj[fieldName] = {};

                // 객체의 필수 필드를 처리
                if (fieldSchema.required && fieldSchema.properties) {
                    fieldSchema.required.forEach(subFieldName => {
                        if (fieldSchema.properties[subFieldName]) {
                            this.createFieldStructure(targetObj[fieldName], subFieldName, fieldSchema.properties[subFieldName], true);
                        }
                    });
                }
            }
            // 배열인 경우 새로운 메서드로 위임
            else if (fieldSchema.type === 'array') {
                this.createCompleteArrayStructure(targetObj, fieldName, fieldSchema);
            }
            // 기본 타입인 경우 기본값 설정
            else {
                // 타입에 따른 기본값 설정
                if (fieldSchema.type === 'string' || (Array.isArray(fieldSchema.type) && fieldSchema.type.includes('string'))) {
                    // fashionSeason 필드는 항상 '사계절'로 하드코딩
                    if (fieldName === 'fashionSeason') {
                        targetObj[fieldName] = '사계절';
                        console.log(`🎯 fashionSeason 필드를 '사계절'로 하드코딩 설정`);
                    }
                    // dropdown 또는 enum이 있는 경우 첫 번째 값 사용
                    else if (fieldSchema.dropdown && fieldSchema.dropdown.length > 0) {
                        // '해당사항없음'이 있으면 우선 사용
                        const defaultIndex = fieldSchema.dropdown.indexOf('해당사항없음');
                        targetObj[fieldName] = defaultIndex >= 0 ? fieldSchema.dropdown[defaultIndex] : fieldSchema.dropdown[0];
                    } else if (fieldSchema.enum && fieldSchema.enum.length > 0) {
                        // '해당사항없음'이 있으면 우선 사용
                        const defaultIndex = fieldSchema.enum.indexOf('해당사항없음');
                        targetObj[fieldName] = defaultIndex >= 0 ? fieldSchema.enum[defaultIndex] : fieldSchema.enum[0];
                    } else {
                        targetObj[fieldName] = '';
                    }
                } else if (fieldSchema.type === 'integer' || fieldSchema.type === 'number' ||
                    (Array.isArray(fieldSchema.type) && (fieldSchema.type.includes('integer') || fieldSchema.type.includes('number')))) {
                    targetObj[fieldName] = 0;
                } else if (fieldSchema.type === 'boolean') {
                    targetObj[fieldName] = false;
                } else {
                    targetObj[fieldName] = null;
                }
            }
        },

        // 🆕 배열 필드의 완전한 구조 생성 (스키마 기반)
        createCompleteArrayStructure(targetObj, fieldName, fieldSchema) {
            console.log(`🔧 ${fieldName} 배열 구조 생성 시작`);

            // 이미 배열이 존재하고 비어있지 않으면 건너뜀
            if (targetObj[fieldName] && Array.isArray(targetObj[fieldName]) && targetObj[fieldName].length > 0) {
                console.log(`⚠️ ${fieldName} 배열이 이미 존재함, 건너뜀`);
                return;
            }

            // 빈 배열로 초기화
            targetObj[fieldName] = [];

            // allOf에서 모든 속성 추출
            if (fieldSchema.allOf && Array.isArray(fieldSchema.allOf)) {
                fieldSchema.allOf.forEach((requirement, index) => {
                    if (requirement.contains && requirement.contains.properties) {
                        const properties = requirement.contains.properties;
                        const nameKey = Object.keys(properties)[0]; // attributeName, noticeItemName 등
                        const valueKey = Object.keys(properties).length > 1 ? Object.keys(properties)[1] : null; // attributeValue, noticeItemValue 등

                        if (properties[nameKey] && properties[nameKey].enum && properties[nameKey].enum.length > 0) {
                            const attributeName = properties[nameKey].enum[0];

                            // 새 항목 생성
                            const newItem = {
                                [nameKey]: attributeName
                            };

                            // 값 키는 모두 null로 설정 (quotationService에서 채울 예정)
                            if (valueKey) {
                                // 비노출속성은 반드시 null로 초기화 (절대 '해당사항없음'으로 채우지 않음)
                                newItem[valueKey] = (fieldName === 'unexposedAttributes') ? null : '해당사항없음';
                            }

                            targetObj[fieldName].push(newItem);
                            console.log(`📝 ${fieldName}[${index}]: ${attributeName} 구조 추가 (값은 null, quotationService에서 처리)`);
                        }
                    }
                });
            }

            console.log(`✅ ${fieldName} 배열 구조 생성 완료: ${targetObj[fieldName].length}개 항목 (모든 값은 null, quotationService에서 처리)`);
        },

        // 🆕 이미지 설정을 문서 구조에 적용하는 메서드
        applyImageSettingsToDocStructure(docStructure, defaultSettings) {
            // 상세 이미지 페이지가 없으면 생성
            if (!docStructure.imagePage.details) {
                docStructure.imagePage.details = { detailedImage: "", htmlProductDetailContent: "", altText: "" };
            }

            // 상세 이미지 HTML 생성 (상단/하단 이미지 포함)
            let detailHtml = '<div style="width:800px; margin:0; padding:0; background-color:#ffffff; box-sizing:border-box;">';

            // 상단 이미지 추가 (설정에서 활성화된 경우)
            if (defaultSettings.imageSettings.useTopImage && defaultSettings.imageSettings.topImageUrl) {
                console.log('📷 상단 이미지 기본 구조에 적용:', defaultSettings.imageSettings.topImageUrl.substring(0, 50) + '...');
                detailHtml += `
                <div style="margin:0 0 10px 0; padding:0; text-align:center; width:800px;">
                    <img src="${defaultSettings.imageSettings.topImageUrl}" alt="상단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
                </div>`;
            }

            // 하단 이미지 추가 (설정에서 활성화된 경우)
            if (defaultSettings.imageSettings.useBottomImage && defaultSettings.imageSettings.bottomImageUrl) {
                console.log('📷 하단 이미지 기본 구조에 적용:', defaultSettings.imageSettings.bottomImageUrl.substring(0, 50) + '...');
                detailHtml += `
                <div style="margin:0; padding:0; text-align:center; width:800px;">
                    <img src="${defaultSettings.imageSettings.bottomImageUrl}" alt="하단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
                </div>`;
            }

            detailHtml += '</div>';

            // 상세 이미지 HTML 설정
            docStructure.imagePage.details.htmlProductDetailContent = detailHtml;

            // 상세 이미지 URL 설정 (topimage와 bottomimage를 포함한 URL 배열)
            const detailImageUrls = [];

            // 상단 이미지 추가 (설정에서 활성화된 경우)
            if (defaultSettings.imageSettings.useTopImage && defaultSettings.imageSettings.topImageUrl) {
                detailImageUrls.push(defaultSettings.imageSettings.topImageUrl);
            }

            // 하단 이미지 추가 (설정에서 활성화된 경우)
            if (defaultSettings.imageSettings.useBottomImage && defaultSettings.imageSettings.bottomImageUrl) {
                detailImageUrls.push(defaultSettings.imageSettings.bottomImageUrl);
            }

            // 상세 이미지 URL 설정 (콤마로 구분된 문자열)
            if (detailImageUrls.length > 0) {
                docStructure.imagePage.details.detailedImage = detailImageUrls.join(',');
            }
        },



        // defaultSettings 적용
        applyDefaultSettings(coupangDoc, defaultSettings) {
            if (!defaultSettings || !coupangDoc.jsonDocument || !coupangDoc.jsonDocument.length) return;

            // 스키마 정보 가져오기
            let schema = null;
            try {
                // 스키마 정보가 있다면 파싱
                if (coupangDoc.rocketRegInfo && coupangDoc.rocketRegInfo.schemaString) {
                    schema = JSON.parse(coupangDoc.rocketRegInfo.schemaString);
                }
            } catch (error) {
                console.error('스키마 파싱 오류:', error);
            }
            // 모든 옵션에 기본 설정 적용
            for (let i = 0; i < coupangDoc.jsonDocument.length; i++) {
                const docBody = coupangDoc.jsonDocument[i];
                console.log(`옵션 ${i+1}/${coupangDoc.jsonDocument.length}에 기본 설정 적용 중...`);
                // 기본 정보 적용
                if (docBody.productPage) {
                    // 브랜드
                    this.applySettingWithDropdownCheck(
                        docBody.productPage,
                        'brand',
                        defaultSettings.brand,
                        schema?.properties?.productPage?.properties?.brand
                    );

                    // 제조사
                    docBody.productPage.manufacturer = defaultSettings.manufacturer || '';

                    // 거래타입
                    this.applySettingWithDropdownCheck(
                        docBody.productPage,
                        'businessType',
                        defaultSettings.transactionType,
                        schema?.properties?.productPage?.properties?.businessType
                    );

                    // 수입여부
                    this.applySettingWithDropdownCheck(
                        docBody.productPage,
                        'importType',
                        defaultSettings.importStatus,
                        schema?.properties?.productPage?.properties?.importType
                    );
                }

                // A/S 정보 적용 (법적 고시 정보)
                if (defaultSettings.asInfo && docBody.legalPage && docBody.legalPage.notices) {
                    const asNotice = docBody.legalPage.notices.find(n => n.noticeItemName === 'A/S 책임자와 전화번호');
                    if (asNotice) {
                        asNotice.noticeItemValue = defaultSettings.asInfo;
                    }
                }

                // 물류 정보 적용
                if (docBody.logisticsPage) {
                    // 박스 내 SKU 수량
                    if (defaultSettings.totalSKUsInBox) {
                        docBody.logisticsPage.totalSKUsInBox = defaultSettings.totalSKUsInBox;
                    }

                    // 유통기간
                    if (defaultSettings.daysToExpiration !== undefined) {
                        docBody.logisticsPage.daysToExpiration = defaultSettings.daysToExpiration;
                    }

                    // 취급주의 사유
                    this.applySettingWithDropdownCheck(
                        docBody.logisticsPage,
                        'specialHandlingReason',
                        defaultSettings.specialHandlingReason,
                        schema?.properties?.logisticsPage?.properties?.specialHandlingReason
                    );
                }

                // 세탁방법 및 취급시 주의사항 적용
                if ((defaultSettings.washingMethod || defaultSettings.handlingPrecautions) &&
                    docBody.legalPage && docBody.legalPage.notices) {

                    const washingNotice = docBody.legalPage.notices.find(n =>
                        n.noticeItemName === '세탁방법 및 취급시 주의사항'
                    );

                    if (washingNotice) {
                        let noticeValue = '';

                        if (defaultSettings.washingMethod && defaultSettings.handlingPrecautions) {
                            noticeValue = `세탁방법: ${defaultSettings.washingMethod}, 취급시 주의사항: ${defaultSettings.handlingPrecautions}`;
                        } else if (defaultSettings.washingMethod) {
                            noticeValue = defaultSettings.washingMethod;
                        } else if (defaultSettings.handlingPrecautions) {
                            noticeValue = defaultSettings.handlingPrecautions;
                        }

                        washingNotice.noticeItemValue = noticeValue;
                    }
                }

                // 제조연월 적용
                if (defaultSettings.manufactureDate && docBody.legalPage && docBody.legalPage.notices) {
                    const dateNotice = docBody.legalPage.notices.find(n =>
                        n.noticeItemName === '출시년월' || n.noticeItemName === '제조년월'
                    );

                    const now = new Date();
                    // 지난달로 설정
                    now.setMonth(now.getMonth() - 1);

                    const year = now.getFullYear();
                    // 월이 한 자릿수일 때 앞에 0을 붙이도록 처리
                    const month = String(now.getMonth() + 1).padStart(2, '0');

                    if (dateNotice) {
                        dateNotice.noticeItemValue = `${year}.${month}`;
                    }
                }

                // 품질보증기준 적용
                if (docBody.legalPage && docBody.legalPage.notices) {
                    const qualityNotice = docBody.legalPage.notices.find(n => n.noticeItemName === '품질보증기준');
                    if (qualityNotice && !qualityNotice.noticeItemValue) {
                        qualityNotice.noticeItemValue = '제품 이상 시 공정거래위원회 고시 소비자분쟁해결기준에 의거하여 보상합니다.';
                    }
                }

                // 상단/하단 이미지 설정 적용
                if (defaultSettings.imageSettings && docBody.imagePage && docBody.imagePage.details) {
                    // 상세 이미지 HTML 생성 (상단/하단 이미지 포함)
                    let detailHtml = '';
                    let hasImages = false;

                    // 기존 HTML 내용이 있으면 유지
                    if (docBody.imagePage.details.htmlProductDetailContent) {
                        detailHtml = docBody.imagePage.details.htmlProductDetailContent;
                        hasImages = true;
                    } else {
                        // 새 HTML 생성
                        detailHtml = '<div style="width:800px; margin:0; padding:0; background-color:#ffffff; box-sizing:border-box;">';
                    }

                    // 상단 이미지 추가 (설정에서 활성화된 경우)
                    if (defaultSettings.imageSettings.useTopImage && defaultSettings.imageSettings.topImageUrl) {
                        // 이미 HTML에 상단 이미지가 포함되어 있는지 확인
                        if (!detailHtml.includes(defaultSettings.imageSettings.topImageUrl)) {
                            console.log('기본 설정 적용 시 상단 이미지 추가:', defaultSettings.imageSettings.topImageUrl.substring(0, 50) + '...');

                            // 기존 HTML이 있으면 앞부분에 추가
                            if (hasImages) {
                                const divEnd = detailHtml.lastIndexOf('</div>');
                                if (divEnd !== -1) {
                                    const beforeDiv = detailHtml.substring(0, divEnd);
                                    const afterDiv = detailHtml.substring(divEnd);
                                    detailHtml = beforeDiv + `
                                    <div style="margin:0 0 10px 0; padding:0; text-align:center; width:800px;">
                                        <img src="${defaultSettings.imageSettings.topImageUrl}" alt="상단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
                                    </div>` + afterDiv;
                                }
                            } else {
                                // 새 HTML에 추가
                                detailHtml += `
                                <div style="margin:0 0 10px 0; padding:0; text-align:center; width:800px;">
                                    <img src="${defaultSettings.imageSettings.topImageUrl}" alt="상단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
                                </div>`;
                            }
                        }
                    }

                    // 하단 이미지 추가 (설정에서 활성화된 경우)
                    if (defaultSettings.imageSettings.useBottomImage && defaultSettings.imageSettings.bottomImageUrl) {
                        // 이미 HTML에 하단 이미지가 포함되어 있는지 확인
                        if (!detailHtml.includes(defaultSettings.imageSettings.bottomImageUrl)) {
                            console.log('기본 설정 적용 시 하단 이미지 추가:', defaultSettings.imageSettings.bottomImageUrl.substring(0, 50) + '...');

                            // 기존 HTML이 있으면 마지막에 추가
                            if (hasImages) {
                                const divEnd = detailHtml.lastIndexOf('</div>');
                                if (divEnd !== -1) {
                                    const beforeDiv = detailHtml.substring(0, divEnd);
                                    const afterDiv = detailHtml.substring(divEnd);
                                    detailHtml = beforeDiv + `
                                    <div style="margin:0; padding:0; text-align:center; width:800px;">
                                        <img src="${defaultSettings.imageSettings.bottomImageUrl}" alt="하단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
                                    </div>` + afterDiv;
                                }
                            } else {
                                // 새 HTML에 추가
                                detailHtml += `
                                <div style="margin:0; padding:0; text-align:center; width:800px;">
                                    <img src="${defaultSettings.imageSettings.bottomImageUrl}" alt="하단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
                                </div>`;

                                // HTML 닫기 태그 추가
                                detailHtml += '</div>';
                            }
                        }
                    } else if (!hasImages) {
                        // 새 HTML 닫기 태그 추가 (하단 이미지가 없는 경우)
                        detailHtml += '</div>';
                    }

                    // 상세 이미지 HTML 설정
                    docBody.imagePage.details.htmlProductDetailContent = detailHtml;

                    // 상세 이미지 URL 설정 (topimage와 bottomimage를 포함한 URL 배열)
                    const detailImageUrls = [];

                    // 상단 이미지 추가 (설정에서 활성화된 경우)
                    if (defaultSettings.imageSettings.useTopImage && defaultSettings.imageSettings.topImageUrl) {
                        detailImageUrls.push(defaultSettings.imageSettings.topImageUrl);
                    }

                    // 하단 이미지 추가 (설정에서 활성화된 경우)
                    if (defaultSettings.imageSettings.useBottomImage && defaultSettings.imageSettings.bottomImageUrl) {
                        detailImageUrls.push(defaultSettings.imageSettings.bottomImageUrl);
                    }

                    // 상세 이미지 URL 설정 (콤마로 구분된 문자열)
                    if (detailImageUrls.length > 0) {
                        docBody.imagePage.details.detailedImage = detailImageUrls.join(',');
                    }
                }
            }
        },

        // dropdown 목록 체크 후 설정값 적용 (새로 추가된 함수)
        applySettingWithDropdownCheck(targetObj, fieldName, value, schemaProp) {
            // 값이 없으면 적용하지 않음
            if (!value) return;

            // brand 필드인 경우, 쉼표로 구분된 값은 첫 번째 값만 사용
            let processedValue = value;
            if (fieldName === 'brand' && typeof value === 'string' && value.includes(',')) {
                processedValue = value.split(',')[0].trim();
                console.log(`브랜드명 '${value}'에서 첫 번째 값 '${processedValue}'만 사용합니다.`);
            }

            // 스키마 속성이 없으면 처리된 값 적용
            if (!schemaProp) {
                targetObj[fieldName] = processedValue;
                return;
            }

            // dropdown 목록 확인
            if (schemaProp.dropdown && Array.isArray(schemaProp.dropdown)) {
                // dropdown 목록에 있는 값인지 확인
                if (schemaProp.dropdown.includes(processedValue)) {
                    targetObj[fieldName] = processedValue;
                } else {
                    // dropdown 목록에 없으면 첫 번째 값 사용
                    targetObj[fieldName] = schemaProp.dropdown[0] || '';
                    console.warn(`경고: '${fieldName}'의 값 '${processedValue}'은(는) dropdown 목록에 없습니다. 대신 '${targetObj[fieldName]}'을(를) 사용합니다.`);
                }
            } else if (schemaProp.enum && Array.isArray(schemaProp.enum)) {
                // enum 목록에 있는 값인지 확인
                if (schemaProp.enum.includes(processedValue)) {
                    targetObj[fieldName] = processedValue;
                } else {
                    // enum 목록에 없으면 첫 번째 값 사용
                    targetObj[fieldName] = schemaProp.enum[0] || '';
                    console.warn(`경고: '${fieldName}'의 값 '${processedValue}'은(는) enum 목록에 없습니다. 대신 '${targetObj[fieldName]}'을(를) 사용합니다.`);
                }
            } else {
                // dropdown이나 enum이 없으면 처리된 값 적용
                targetObj[fieldName] = processedValue;
            }
        },

        // 상세 이미지 추출
        extractDetailImages(description) {
            if (!description) return [];

            const parser = new DOMParser();
            const doc = parser.parseFromString(description, 'text/html');
            const imgElements = doc.querySelectorAll('img');
            return Array.from(imgElements)
                .map(img => {
                    let src = img.src;
                    if (!src) return null;

                    // 이미지 확장자(.jpg, .jpeg, .png, .gif, .webp) 뒤의 쿼리 파라미터 제거
                    // 예: https://...jpg?__r__=123 → https://...jpg
                    const cleanedSrc = src.replace(/(\.(jpg|jpeg|png|gif|webp))([?#].*)?$/i, '$1');
                    return cleanedSrc;
                })
                .filter(src => src && src.length > 10);
        },

        // 상세 이미지에 상단/하단 이미지 추가 함수
        getDetailImagesWithTopBottom(detailImages) {
            if (!this.defaultSettings || !this.defaultSettings.imageSettings) {
                return detailImages || [];
            }

            const result = [...detailImages]; // 기존 이미지 복사

            // 상단 이미지 추가 (설정에서 활성화된 경우)
            if (this.defaultSettings.imageSettings.useTopImage && this.defaultSettings.imageSettings.topImageUrl) {
                // 이미 배열에 없는 경우에만 추가
                if (!result.includes(this.defaultSettings.imageSettings.topImageUrl)) {
                    console.log('상세 이미지에 상단 이미지 추가:', this.defaultSettings.imageSettings.topImageUrl.substring(0, 50) + '...');
                    // 배열 맨 앞에 추가
                    result.unshift(this.defaultSettings.imageSettings.topImageUrl);
                }
            }

            // 하단 이미지 추가 (설정에서 활성화된 경우)
            if (this.defaultSettings.imageSettings.useBottomImage && this.defaultSettings.imageSettings.bottomImageUrl) {
                // 이미 배열에 없는 경우에만 추가
                if (!result.includes(this.defaultSettings.imageSettings.bottomImageUrl)) {
                    console.log('상세 이미지에 하단 이미지 추가:', this.defaultSettings.imageSettings.bottomImageUrl.substring(0, 50) + '...');
                    // 배열 맨 뒤에 추가
                    result.push(this.defaultSettings.imageSettings.bottomImageUrl);
                }
            }

            return result;
        },

        // 등록번호 생성 — 서버가 회사별·날짜별 카운터로 채번한다(issueRegistrationId).
        // 화면 목록(조회 기간으로만 채워짐)에서 최대값을 세면, 기간이 오늘을 안 담을 때 같은 번호가 반복된다.
        async generateRegistrationId(timestamp) {
            const date = new Date(timestamp);
            const year = date.getFullYear().toString().slice(-2);
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const res = await axios.post('/api/rocketReg/issueRegistrationId', { datePrefix: `${year}${month}${day}` });
            if (!res.data || res.data.res !== 'success' || !res.data.registrationId) {
                throw new Error((res.data && res.data.alert) || '등록번호를 받지 못했습니다.');
            }
            return res.data.registrationId;
        },

        // 옵션 처리
        // keepSkuIds(소싱에서 남긴 1688 skuId)가 있으면 그 옵션만 만든다. 하나도 안 맞으면 전부 등록하지 않고 실패시킨다.
        // 「모든 옵션 가격이 같은가」는 **거르기 전 전체 SKU** 로 본다 — 그래야 소싱 도구(getOfferDetail regPriceCny)가
        // 미리 계산한 값과 등록가가 항상 같다. 거르기는 그 판정 뒤, 옵션을 만들기(map) 직전에 한다.
        processOptions(data, regId, keepSkuIds = null) {
            let keptSkus = null;
            if (keepSkuIds && keepSkuIds.length) {
                const picked = pickKeptSkus(data.productSkuInfos, keepSkuIds);
                if (!picked.skus.length) throw new Error(KEEP_NONE_MESSAGE);
                if (picked.missing.length) console.error('[AIRocketReg] 남길 옵션 중 1688 상품에 없는 번호:', picked.missing);
                keptSkus = picked.skus;
            }
            // SKU가 없는 경우(단일 상품)
            if (!data.productSkuInfos || data.productSkuInfos.length === 0) {
                const singleOption = {
                    id: `default-${Date.now()}`,
                    optionNumber: `${regId.substring(0, 9)}001`,  // 등록번호 앞 9자리 + 001
                    name: data.subject,
                    nameKor: data.subjectTrans,
                    stock: data.productSaleInfo.amountOnSale,
                    // 가격 정보를 priceRangeList의 첫 번째 항목에서 가져옴
                    price: parseFloat(data.productSaleInfo.priceRangeList[0].price),
                    status: 'pending',
                    mainImageStatus: {
                        status: 'pending',
                        image: data.productImage.images[0],
                        processedImage: null,
                        textRemoved: false,
                        dimensions: '', // 아직 그림 안 받음 — 못 잼 = 빈 칸(지어낸 기본 크기 0)
                        filesize: ''
                    },
                    additionalImageStatus: {
                        status: 'pending',
                        images: data.productImage.images
                    },
                    detailImageStatus: {
                        status: 'pending',
                        images: data.description ? this.getDetailImagesWithTopBottom(this.extractDetailImages(data.description)) : []
                    },
                    koreanLabelStatus: 'pending',
                    pricingStatus: 'pending',
                    seoStatus: 'pending',
                    quotationStatus: 'pending'
                };

                // 가격 정보 계산 및 업데이트
                return [this.updateOptionPricing(singleOption)];
            }

            // SKU가 여러 개 있는 경우 - 가격 결정 로직 개선
            // 1. 모든 SKU의 가격이 동일한지 확인
            const skuPrices = data.productSkuInfos.map(sku =>
                parseFloat((sku.fenxiaoPriceInfo && sku.fenxiaoPriceInfo.offerPrice)
                    ? sku.fenxiaoPriceInfo.offerPrice
                    : sku.consignPrice)
            );

            const allSamePrices = skuPrices.every(price => price === skuPrices[0]);
            const basePrice = parseFloat(data.productSaleInfo.priceRangeList[0].price);

            // 2. 모든 SKU 가격이 동일하면 productSaleInfo의 기본 가격 사용
            const shouldUseBasePrice = allSamePrices;

            console.log(`가격 결정 로직: 모든 SKU 가격 동일(${allSamePrices}) -> ${shouldUseBasePrice ? `기본가격 ${basePrice} 사용` : 'SKU별 가격 사용'}`);

            // 뺀 옵션의 SKU 사진과 같은 주소인 사진은 추가·상세 사진에서 뺀다(4DBBFE · 주소가 다른 사진은 어느 옵션 것인지 몰라 그대로)
            const keptImages = list => dropExcludedSkuImages(list, data.productSkuInfos, keepSkuIds).images;
            const gallery = keptImages(data.productImage.images);
            return (keptSkus || data.productSkuInfos).map((sku, index) => {
                // 옵션 번호 형식: 등록번호 앞 9자리 + 3자리 옵션 시퀀스
                const optionNumber = `${regId.substring(0, 9)}${String(index + 1).padStart(3, '0')}`;

                // 옵션 이미지를 결정
                const optionImage = sku.skuAttributes[0].skuImageUrl
                    || (sku.skuAttributes[1] && sku.skuAttributes[1].skuImageUrl)
                    || gallery[0];

                // 가격 정보 가져오기 - 개선된 로직
                const price = shouldUseBasePrice
                    ? basePrice
                    : parseFloat((sku.fenxiaoPriceInfo && sku.fenxiaoPriceInfo.offerPrice)
                        ? sku.fenxiaoPriceInfo.offerPrice
                        : sku.consignPrice);

                const option = {
                    id: sku.skuId,
                    optionNumber,
                    option1: {
                        name: sku.skuAttributes[0].value,
                        nameTrans: sku.skuAttributes[0].valueTrans,
                        imageUrl: sku.skuAttributes[0].skuImageUrl || ''
                    },
                    option2: sku.skuAttributes[1] ? {
                        name: sku.skuAttributes[1].value,
                        nameTrans: sku.skuAttributes[1].valueTrans,
                        imageUrl: sku.skuAttributes[1].skuImageUrl || ''
                    } : null,
                    stock: sku.amountOnSale,
                    price: price, // 수정된 부분: 위에서 계산한 가격 정보 사용
                    mainImageStatus: {
                        status: 'pending',
                        image: optionImage,
                        processedImage: null,
                        textRemoved: false,
                        dimensions: '', // 아직 그림 안 받음 — 못 잼 = 빈 칸(지어낸 기본 크기 0)
                        filesize: ''
                    },
                    additionalImageStatus: {
                        status: 'pending',
                        images: gallery
                    },
                    detailImageStatus: {
                        status: 'pending',
                        images: data.description ? keptImages(this.getDetailImagesWithTopBottom(this.extractDetailImages(data.description))) : []
                    },
                    koreanLabelStatus: 'pending',
                    pricingStatus: 'pending',
                    seoStatus: 'pending',
                    sizeChartStatus: 'pending',
                    quotationStatus: 'pending'
                };

                // 가격 계산 및 업데이트 후 반환
                return this.updateOptionPricing(option);
            });
        },

        // 옵션 가격 업데이트
        //
        // policy 를 받으면 그 정책으로 계산한다. 상품에 저장된 설정(pricing.info)이 있는 옵션을
        // 화면 기본세팅으로 계산하면 셀러가 넣은 환율이 조용히 덮인다 — 부르는 쪽이
        // pricingPolicy.resolve(product, this.defaultSettings) 를 넘긴다.
        // 상품이 아직 없는 자리(상품 생성 중)에서는 안 넘기고, 그때만 화면 기본세팅으로 떨어진다.
        //
        // keepBundle 이 참이면(소싱 묶음 수량 표식이 있는 상품) 자동 번들 계산을 건너뛰고 옵션의 수량을 지킨다.
        //
        // fixedSellingPrice 가 있으면(소싱 목표 판매가 표식이 있는 상품) 판매가를 그 값으로 고정하고
        // 공급가 = 판매가 × (1 − 쿠팡마진율) 로 푼다. 마진율·최소공급마진 보정은 쓰지 않는다.
        // 공급마진이 마진 기준(고객 설정 rocketMarginGate · 없으면 판매가의 25%)에 못 미치면 옵션을 건드리지 않고 에러를 던진다 (pricingPolicy.fixedSellingPricing).
        // fixedPurchaseCost(소싱 매입원가 sourcingPurchaseCost)가 있으면 매입가는 CNY × 환율 대신 그 값이다.
        // fixedSupplyPrice(소싱 공급가 sourcingSupplyPrice)가 있으면 공급가는 재계산 없이 그 값이다.
        // refCny(가장 싼 옵션의 개당 CNY)를 주면 소싱 값은 그 옵션 기준이고, 더 비싼 옵션은
        // pricingPolicy.scaleFixedPricing 으로 판매가·매입원가를 올린다.
        // purchaseCostBundle(소싱 묶음 수량 sourcingBundleQuantity)은 fixedPurchaseCost 가 계산된 묶음 수량이다 —
        // 옵션 묶음이 그와 다르면 매입가를 개당 원가 × 묶음 수량으로 맞춘다(pricingPolicy.bundledPurchaseCost).
        updateOptionPricing(option, policy = null, keepBundle = false, fixedSellingPrice = null, fixedPurchaseCost = null, fixedSupplyPrice = null, refCny = null, purchaseCostBundle = null) {
            const settings = policy || pricingPolicy.resolve(null, this.defaultSettings);

            // 중국 위안화 가격
            const cnyPrice = option.price;

            // 번들링 계산
            // 옵션에 이미 정해진 묶음 수량이 있으면 그걸 출발값으로 쓴다.
            // 예전엔 무조건 1 로 시작해서, 묶음을 3 으로 지정해둔 뒤 가격 재계산을 부르면
            // 묶음이 조용히 전부 1 로 풀렸다 (2026-09-17 실측: 24개 옵션 bundleQuantity 3→1).
            // 새로 만들어지는 옵션에는 bundleQuantity 가 없으므로 기존대로 1 에서 시작한다.
            const savedBundleQuantity = Number(option.bundleQuantity);
            let bundleQuantity = Number.isFinite(savedBundleQuantity) && savedBundleQuantity >= 1
                ? savedBundleQuantity
                : 1; // 기본 번들 수량

            // 자동 번들링이 켜져 있으면 지금까지처럼 자동계산이 이긴다 (기존 동작 유지)
            // 단, 소싱에서 묶음 수량이 정해진 상품은 그 값을 지킨다.
            if (settings.enableBundling && !keepBundle) {
                bundleQuantity = this.calculateBundleQuantity(
                    cnyPrice,
                    settings.exchangeRate,
                    settings.marginRate,
                    settings.coupangMarginRate,
                    settings.bundlingType,
                    settings.minMarginAmount,
                    settings.minCoupangMarginAmount
                );
            }

            // 번들링된 가격으로 계산
            const bundledCnyPrice = cnyPrice * bundleQuantity;
            if (fixedSellingPrice) {
                ({ sellingPrice: fixedSellingPrice, purchaseCost: fixedPurchaseCost, supplyPrice: fixedSupplyPrice } =
                    pricingPolicy.scaleFixedPricing({ sellingPrice: Number(fixedSellingPrice), purchaseCost: fixedPurchaseCost, supplyPrice: fixedSupplyPrice },
                        cnyPrice, refCny, bundleQuantity, settings, purchaseCostBundle));
            }
            const purchasePrice = (fixedSellingPrice && fixedPurchaseCost)
                ? Number(fixedPurchaseCost)
                : pricingPolicy.purchaseCost(bundledCnyPrice, settings);

            let supplyPrice, sellingPrice, supplyMargin, sellingMargin;

            if (fixedSellingPrice) {
                // 소싱 목표 판매가 고정 — 역마진이면 여기서 던져 옵션에 아무것도 쓰지 않는다
                ({ supplyPrice, sellingPrice, supplyMargin, sellingMargin } = pricingPolicy.fixedSellingPricing(
                    Number(fixedSellingPrice), purchasePrice, settings, `옵션 ${option.optionNumber}`, fixedSupplyPrice,
                    this.$store?.state?.settings?.[0]?.rocketMarginGate));
            } else if (settings.enableMinSupplyMargin) {
                // 최소 공급 마진 보장이 활성화된 경우
                // 새로운 로직: 쿠팡 마진율 고정, 최소 공급 마진액 보장
                supplyPrice = purchasePrice + settings.minSupplyMarginAmount;
                supplyPrice = Math.round(supplyPrice / settings.roundUnit) * settings.roundUnit;

                // 판매가 = 공급가 ÷ (1 - 쿠팡마진율)
                const coupangRate = settings.coupangMarginRate / 100;
                const rawSellingPrice = supplyPrice / (1 - coupangRate);
                sellingPrice = Math.round(rawSellingPrice / settings.roundUnit) * settings.roundUnit;

                // 마진 계산
                supplyMargin = supplyPrice - purchasePrice;
                sellingMargin = sellingPrice - supplyPrice;

                console.log(`최소 공급 마진 보장 계산 (옵션 ${option.optionNumber}):`, {
                    원가: purchasePrice,
                    최소공급마진: settings.minSupplyMarginAmount,
                    공급가: supplyPrice,
                    실제공급마진: supplyMargin,
                    쿠팡마진율: settings.coupangMarginRate + '%',
                    판매가: sellingPrice,
                    쿠팡마진: sellingMargin
                });
            } else {
                // 기존 로직: 마진율 기반 계산
                const marginRate = settings.marginRate / 100;
                const rawSupplyPrice = purchasePrice / (1 - marginRate);
                supplyPrice = Math.round(rawSupplyPrice / settings.roundUnit) * settings.roundUnit;

                // 판매가 계산 (공급가 ÷ (1 - 쿠팡마진율/100))
                const coupangRate = settings.coupangMarginRate / 100;
                const rawSellingPrice = supplyPrice / (1 - coupangRate);
                sellingPrice = Math.round(rawSellingPrice / settings.roundUnit) * settings.roundUnit;

                // 마진 계산
                supplyMargin = supplyPrice - purchasePrice;
                sellingMargin = sellingPrice - supplyPrice;
            }

            // 옵션 객체에 가격 정보 추가/업데이트
            option.supplyPrice = supplyPrice;
            option.sellingPrice = sellingPrice;
            option.purchasePrice = purchasePrice;
            option.supplyMargin = supplyMargin;
            option.sellingMargin = sellingMargin;
            option.bundleQuantity = bundleQuantity;

            return option;
        },

        // 소싱에서 정한 묶음 수량을 옵션에 싣고 그 수량으로 가격을 다시 계산한다 (자동 번들 계산은 건너뛴다)
        applySourcingBundleQuantity(options, quantity, policy = null) {
            (options || []).forEach(option => {
                option.bundleQuantity = quantity;
                this.updateOptionPricing(option, policy, true);
            });
            return options;
        },

        // 소싱에서 정한 목표 판매가로 옵션 가격을 다시 계산한다 (묶음 수량은 옵션 값을 지킨다).
        // 공급마진이 마진 기준(고객 설정 · 없으면 25%)에 못 미치는 옵션이 하나라도 있으면 어느 옵션도 바꾸기 전에 실패한다.
        applySourcingSellingPrice(options, sellingPrice, purchaseCost = null, policy = null, supplyPrice = null, purchaseCostBundle = null) {
            const refCny = pricingPolicy.referenceCny(options);
            (options || []).forEach(option => this.updateOptionPricing({ ...option }, policy, true, sellingPrice, purchaseCost, supplyPrice, refCny, purchaseCostBundle));
            (options || []).forEach(option => this.updateOptionPricing(option, policy, true, sellingPrice, purchaseCost, supplyPrice, refCny, purchaseCostBundle));
            return options;
        },

        // 번들 수량 계산
        calculateBundleQuantity(cnyPrice, exchangeRate, marginRate, coupangMarginRate, bundlingType, minMarginAmount, minCoupangMarginAmount) {
            // 단일 상품으로 계산한 가격
            const purchasePrice = cnyPrice * exchangeRate;
            const marginRateDecimal = marginRate / 100;
            const coupangRateDecimal = coupangMarginRate / 100;

            const rawSupplyPrice = purchasePrice / (1 - marginRateDecimal);
            const supplyPrice = Math.round(rawSupplyPrice / 100) * 100;

            const rawSellingPrice = supplyPrice / (1 - coupangRateDecimal);
            const sellingPrice = Math.round(rawSellingPrice / 100) * 100;

            // 마진 계산
            const supplyMargin = supplyPrice - purchasePrice;
            const sellingMargin = sellingPrice - supplyPrice;

            // 번들링 기준에 따른 조건 확인
            let bundleQuantity = 1;

            if (bundlingType === 'marginAmount') {
                // 공급 마진액 기준
                if (supplyMargin < minMarginAmount) {
                    // 2개, 4개, 6개... 단위로 번들링
                    while (supplyMargin * bundleQuantity < minMarginAmount && bundleQuantity < 20) {
                        bundleQuantity += 2;
                    }
                }
            } else { // 'coupangMarginAmount'
                // 쿠팡 마진액 기준
                if (sellingMargin < minCoupangMarginAmount) {
                    // 2개, 4개, 6개... 단위로 번들링
                    while (sellingMargin * bundleQuantity < minCoupangMarginAmount && bundleQuantity < 20) {
                        bundleQuantity += 2;
                    }
                }
            }

            return bundleQuantity;
        },

        // 새 상품 문서 하나 — 등록번호를 받고 옵션·가격·쿠팡 문서까지 채운다(저장 전).
        // this.newProduct(link·keyword·brand·categoryPath·categoryCode·productFeature)와 1688 상품정보(data)·쿠팡 양식(extensionData)으로 만든다.
        async buildRocketRegProduct(data, extensionData) {
            const now = new Date().getTime();

            // 등록번호 생성
            const newRegistrationId = await this.generateRegistrationId(now);

            // 옵션 처리 — 대기열 항목에 남길 옵션이 실려 왔으면 그 옵션만 만든다.
            // 상품에 sourcingSkuIds 표식을 남겨, 옵션을 다시 만들 때(초기화)도 같은 옵션만 남긴다.
            const sourcingSkuIds = this.queueAutoMode ? (this._currentQueueKeepSkuIds || null) : null;
            const processedOptions = this.processOptions(data, newRegistrationId, sourcingSkuIds);

            // 대기열 항목에 소싱 묶음 수량이 실려 왔으면 그 수량으로 옵션 가격을 만든다.
            // 상품에 sourcingBundleQuantity 표식을 남겨, 이후 가격 작업이 자동 번들 계산으로 덮지 않게 한다.
            const sourcingBundleQuantity = this.queueAutoMode ? (this._currentQueueBundleQuantity || null) : null;
            if (sourcingBundleQuantity) {
                this.applySourcingBundleQuantity(processedOptions, sourcingBundleQuantity);
            }
            // 소싱 목표 판매가가 실려 왔으면 판매가를 그 값으로 고정한다(역마진이면 여기서 실패).
            // sourcingSellingPrice 표식이 이후 가격 작업에서도 판매가를 지킨다.
            const sourcingSellingPrice = this.queueAutoMode ? (this._currentQueueSellingPrice || null) : null;
            // 소싱 매입원가는 목표 판매가와 같이 올 때만 쓴다(고정가 상품의 마진 검사용).
            const sourcingPurchaseCost = sourcingSellingPrice ? (this._currentQueuePurchaseCost || null) : null;
            // 소싱에서 확정한 공급가도 목표 판매가와 같이 올 때만 쓴다. 등록 단계는 재계산하지 않는다.
            const sourcingSupplyPrice = sourcingSellingPrice ? (this._currentQueueSupplyPrice || null) : null;
            if (sourcingSellingPrice) {
                this.applySourcingSellingPrice(processedOptions, sourcingSellingPrice, sourcingPurchaseCost, null, sourcingSupplyPrice, sourcingBundleQuantity);
            }

            // 브랜드명 설정 - 쉼표로 구분된 경우 첫 번째 브랜드만 사용
            const getFirstBrandName = (brandName) => {
                if (brandName && brandName.includes(',')) {
                    const firstBrand = brandName.split(',')[0].trim();
                    console.log('상품 생성 시 쉼표로 구분된 브랜드명에서 첫 번째 값만 사용:', firstBrand);
                    return firstBrand;
                }
                return brandName;
            };
            const brandName = this.newProduct.brand || getFirstBrandName(this.defaultSettings.brand);
            // 추가 사진(mainImages)·상세 사진에서 뺀 옵션의 SKU 사진을 뺀다(4DBBFE — 남긴 «술장식 없음» 인데 추가·상세가 뺀 «술장식 있음» 사진)
            const keptImages = list => dropExcludedSkuImages(list, data.productSkuInfos, sourcingSkuIds).images;
            const gallery = keptImages(data.productImage.images);

            // 새 상품 객체 기본 구조
            const newProductObj = {
                createdAt: now,
                registrationId: newRegistrationId,
                productImage: gallery[0],
                productName: data.subject,
                productNameTrans: data.subjectTrans,
                categoryPath: this.newProduct.categoryPath || extensionData.categoryPath,
                categoryCode: this.newProduct.categoryCode || extensionData.categoryCode,
                rocketRegInfo: extensionData.rocketRegInfo,
                mainImageStatus: 'pending',
                additionalImageStatus: 'pending',
                detailImageStatus: 'pending',
                koreanLabelStatus: 'pending',
                pricingStatus: 'pending',
                seoStatus: 'pending',
                sizeChartStatus: 'pending',
                quotationStatus: 'pending',
                targetKeyword: this.newProduct.keyword,
                brand: brandName,
                productFeature: this.newProduct.productFeature,
                productShippingInfo: data.productShippingInfo,
                구매링크: this.newProduct.link.split('?')[0],
                options: processedOptions,
                // 추가 정보
                originalPrice: data.productSaleInfo.priceRangeList[0].price,
                minOrderQuantity: data.minOrderQuantity,
                attributes: data.productAttribute,
                mainImages: gallery,
                detailImages: keptImages(this.getDetailImagesWithTopBottom(this.extractDetailImages(data.description))),
                // 등록 정보 복사
                manufacturer: this.defaultSettings.manufacturer,
                transactionType: this.defaultSettings.transactionType,
                importStatus: this.defaultSettings.importStatus,
                asInfo: this.defaultSettings.asInfo,
                exchangeRate: this.defaultSettings.exchangeRate,
                marginRate: this.defaultSettings.marginRate,
                coupangMarginRate: this.defaultSettings.coupangMarginRate,
                roundUnit: this.defaultSettings.roundUnit,
                createDate: now,
                updateDate: now
            };
            if (sourcingBundleQuantity) newProductObj.sourcingBundleQuantity = sourcingBundleQuantity;
            // 소싱 벤치 옵션ID — 탈환의깃발 09 기록이 rocket.sourcing.listRegistrations(benchmarkVendorItemId)로 다시 얻는다
            if (this.queueAutoMode && this._currentQueueBenchmarkVendorItemId) newProductObj.sourcingBenchmarkVendorItemId = this._currentQueueBenchmarkVendorItemId;
            if (sourcingSellingPrice) newProductObj.sourcingSellingPrice = sourcingSellingPrice;
            if (sourcingPurchaseCost) newProductObj.sourcingPurchaseCost = sourcingPurchaseCost;
            if (sourcingSupplyPrice) newProductObj.sourcingSupplyPrice = sourcingSupplyPrice;
            if (sourcingSkuIds) newProductObj.sourcingSkuIds = sourcingSkuIds;

            // 상품 레벨에서 통합 coupangDoc 설정
            newProductObj.coupangDoc = this.initializeCoupangDoc(
                extensionData.createResult,
                this.defaultSettings,
                extensionData.rocketRegInfo,
                processedOptions // 모든 옵션 정보 전달
            );
            return newProductObj;
        },

        // AIRocketReg.vue의 getStatusVariant 메서드 수정
        getStatusVariant(status) {
            // 콘솔에 현재 처리 중인 상태 출력 (디버깅용)
            // console.log('현재 상태:', status);

            switch (status) {
                case 'completed': return 'success';
                case 'partially_completed': return 'info';
                case 'processing': return 'primary';
                case 'pending': return 'secondary';
                case 'error': return 'danger';
                case 'disabled': return 'light';
                default: return 'secondary';
            }
        },

        // AIRocketReg.vue - getStatusText 메서드 수정
        getStatusText(status) {
            const texts = {
                completed: '완료',
                partially_completed: '일부완료',
                processing: '진행중',
                pending: '대기',
                error: '오류',
                disabled: '미사용'
            };

            // status가 객체인 경우(mainImageStatus 등) status.status 값을 사용
            if (status && typeof status === 'object' && status.status) {
                return texts[status.status] || '대기';
            }

            return texts[status] || '대기';
        },

        // 양식(schemaString)이 사이즈표 사진을 요구하나
        isSizeChartRequired(product) {
            // 목록용 문서엔 rocketRegInfo 가 없다 — 서버가 미리 판정해 준 값을 쓴다
            if (product && product._light) return !!product._sizeChartRequired;
            try {
                if (product.rocketRegInfo && product.rocketRegInfo.schemaString) {
                    const schema = JSON.parse(product.rocketRegInfo.schemaString);

                    // 스키마의 imagePage > images > required 배열에 sizeChartImage가 있는지 확인
                    if (schema.properties &&
                        schema.properties.imagePage &&
                        schema.properties.imagePage.properties &&
                        schema.properties.imagePage.properties.images &&
                        schema.properties.imagePage.properties.images.properties &&
                        schema.properties.imagePage.properties.images.required) {

                        return schema.properties.imagePage.properties.images.required.includes('sizeChartImage');
                    }
                }
            } catch (error) {
                console.error('스키마 파싱 오류:', error);
            }
            return false; // 기본값은 필수 아님
        },
    }
};
